/**
 * The sentence a person reads when something failed, from the facts of the
 * failure alone.
 *
 * Pure on purpose: it imports only `t`, never `@/lib/api` (which pulls
 * `expo/fetch` and cannot load under `bun test`). `failure-messages.ts` reads
 * the facts off a thrown value and hands them here; the developer's detail
 * (the endpoint, the status, the advice about an old server process) goes to
 * the console in development and never into a sentence.
 *
 * The rule: say what went wrong, and what to do about it, in words a shop
 * owner can act on. No status codes, no commands, no hosts. First match wins.
 */
import type { AppKey } from '@alluzo/shared';

import { t } from '@/lib/i18n';

export type FailureFacts = {
  /** No answer at all: the request never reached anything. */
  readonly transport: boolean;
  /** The HTTP status, when there was an answer. */
  readonly status: number | null;
  /** The refusal's code (`ApiError.code` or Better Auth's). */
  readonly code: string | null;
  /** The server's own sentence, when it sent one. */
  readonly message: string | null;
  /** An error the app raised for itself: its own sentence is the true one. */
  readonly own: string | null;
  /** A Better Auth call (sign in, sign up, sign out). */
  readonly auth: boolean;
};

/**
 * Every Better Auth code this app can provoke with email and password.
 * Source: `@better-auth/core`'s `BASE_ERROR_CODES`. Keys are literals so `t`
 * stays typed and `i18n:add` finds them.
 */
const AUTH_WORDS = {
  INVALID_EMAIL_OR_PASSWORD: 'failure.auth.invalid_email_or_password',
  INVALID_EMAIL: 'failure.auth.invalid_email',
  INVALID_PASSWORD: 'failure.auth.invalid_password',
  USER_NOT_FOUND: 'failure.auth.user_not_found',
  USER_ALREADY_EXISTS: 'failure.auth.user_already_exists',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'failure.auth.user_already_exists_use_another_email',
  CREDENTIAL_ACCOUNT_NOT_FOUND: 'failure.auth.credential_account_not_found',
  EMAIL_NOT_VERIFIED: 'failure.auth.email_not_verified',
  PASSWORD_TOO_SHORT: 'failure.auth.password_too_short',
  PASSWORD_TOO_LONG: 'failure.auth.password_too_long',
  SESSION_EXPIRED: 'failure.auth.session_expired',
  FAILED_TO_CREATE_USER: 'failure.auth.not_now',
  FAILED_TO_CREATE_SESSION: 'failure.auth.not_now',
  // An origin refusal is our own configuration, never the person's doing.
  INVALID_ORIGIN: 'failure.our_side',
  VALIDATION_ERROR: 'failure.auth.validation_error',
  MISSING_FIELD: 'failure.auth.missing_field',
} as const satisfies Record<string, AppKey>;

const authWordsOf = (code: string | null): AppKey | null =>
  code !== null && Object.hasOwn(AUTH_WORDS, code) ? AUTH_WORDS[code as keyof typeof AUTH_WORDS] : null;

/**
 * The codes of a request this app's own code built wrong, beside every
 * `BAD_*`. The server's sentence for each is written for a developer ("Drop
 * the header.") and the person cannot act on it.
 */
const BUILT_WRONG: ReadonlySet<string> = new Set(['TENANT_REQUIRED', 'TENANT_HEADER_NOT_ALLOWED', 'AGENT_BAD_MESSAGES']);

/** A request this app's own code built wrong: the person cannot fix it. */
export const builtWrong = (code: string | null): boolean => code !== null && (BUILT_WRONG.has(code) || code.startsWith('BAD_'));

/**
 * The server's own sentence, or null. A refusal with no envelope reaches the
 * client as the bare "HTTP 400" `api.ts` falls back to: that is a status, not
 * a sentence, and never a person's to read.
 */
const sentenceOf = (message: string | null): string | null => (message === null || /^HTTP \d{3}$/.test(message) ? null : message);

export const failureWordsOf = (facts: FailureFacts): string => {
  const { transport, status, code, own, auth } = facts;
  const message = sentenceOf(facts.message);
  if (transport) return t('failure.unreachable');
  if (own !== null) return own;
  const authKey = auth ? authWordsOf(code) : null;
  if (authKey !== null) return t(authKey);
  if (status === 401) return t('failure.signed_out');
  if (status === 403) return message ?? t('failure.not_allowed');
  if (status === 404) return code !== null && message !== null ? message : t('failure.out_of_date');
  if (status === 429) return t('failure.too_often');
  if (status !== null && status >= 500) return t('failure.our_side');
  if (status === 400 && builtWrong(code)) return t('failure.refused');
  if (message !== null) return message;
  return t('failure.refused');
};
